import { useState, useEffect, useRef } from "react";

// Shared fields to search across — same as the filter predicate
const getSearchableText = (item) =>
  [
    item?.account_no,
    item?.name,
    item?.ledger_name,
    item?.alias_name,
    item?.alise_name,
    item?.group_name,
    item?.voucher_type_name,
    item?.symbol,
    item?.opening_balance?.toString(),
    item?.calculated_closing_balance?.toString(),
    item?.mobile_no?.toString(),
    item?.aadhaar_no?.toString(),
    item?.aadhaar?.toString(),
    item?.under_name,
    item?.city,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/\s+/g, "");

const hasAnyMatch = (dataList, search) => {
  const cleaned = search.toLowerCase().replace(/\s+/g, "");
  if (!cleaned) return true;
  return (dataList || []).some((item) =>
    getSearchableText(item).includes(cleaned),
  );
};

export const useDropdownFilter = ({
  dataList,
  searchInput,
  inputRef,
  isDynamic,
  defaultItem = { id: "1", name: "Not Applicable" },
}) => {
  const [filteredData, setFilteredData] = useState([]);

  // Keep a live ref to dataList so the keydown handler always sees latest data
  const dataListRef = useRef(dataList);
  useEffect(() => {
    dataListRef.current = dataList;
  }, [dataList]);

  // Attach typeahead restriction to the input
  useEffect(() => {
    const inputEl = inputRef?.current || (inputRef?.tagName ? inputRef : null);
    if (!inputEl) return;

    const ALWAYS_ALLOW = new Set([
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Escape",
      "Enter",
      "Tab",
      "F1",
      "F2",
      "F3",
      "F4",
      "F5",
      "F6",
      "F7",
      "F8",
      "F9",
      "F10",
      "F11",
      "F12",
    ]);

    const handleKeyDown = (e) => {
      if (ALWAYS_ALLOW.has(e.key) || e.ctrlKey || e.altKey || e.metaKey) return;
      if (e.key.length !== 1) return; // non-printable
      // Skip restriction if data hasn't loaded yet
      if (!dataListRef.current || dataListRef.current.length === 0) return;

      const { value, selectionStart, selectionEnd } = inputEl;
      const previewValue =
        value.slice(0, selectionStart) + e.key + value.slice(selectionEnd);

      if (!hasAnyMatch(dataListRef.current, previewValue)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    inputEl.addEventListener("keydown", handleKeyDown);
    return () => inputEl.removeEventListener("keydown", handleKeyDown);
  }, [inputRef]);

  useEffect(() => {
    const cleanedSearchInput =
      searchInput?.toLowerCase()?.replace(/\s+/g, "") || "";

    const filteredSuggestions = dataList?.filter((item) => {
      const searchableText = getSearchableText(item);
      return searchableText.includes(cleanedSearchInput);
    });

    const currentRef = inputRef?.current || inputRef;
    const selectionStart = currentRef?.selectionStart;
    const selectionEnd = currentRef?.selectionEnd;

    const isFullSelection =
      searchInput !== "" &&
      selectionStart === 0 &&
      selectionEnd === searchInput?.length;

    const shouldUseFilteredData = searchInput !== "" && !isFullSelection;

    setFilteredData(shouldUseFilteredData ? filteredSuggestions : dataList);
  }, [
    dataList,
    searchInput,
    isDynamic ? inputRef?.selectionStart : inputRef?.current?.selectionStart,
    isDynamic ? inputRef?.selectionEnd : inputRef?.current?.selectionEnd,
  ]);

  return { filteredData };
};
